import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconMusic, 
  IconBriefcase, 
  IconAlertCircle, 
  IconPlus,
  IconClockHour4,
  IconVinyl,
  IconStar,
  IconDevices,
  IconCurrencyEuro,
  IconLink,
  IconWorld,
  IconBrandSoundcloud,
  IconBrandSpotify,
  IconBrandBandcamp,
  IconCloud,
  IconBrandInstagram,
  IconEdit,
  IconCalendarEvent,
  IconCamera,
  IconShieldCheck,
  IconGlass
} from '@tabler/icons-react';
import { MainLayout } from '../components';
import StaffProfileForm from '../components/StaffProfileForm';
import ProfileNavigation from '../components/ProfileNavigation';
import { StaffProfile, StaffApplication, STAFF_TYPE_LABELS, MUSIC_GENRE_LABELS } from '../types/staff';
import { staffService } from '../services/api';
import styles from './StaffProfilePage.module.css';

const StaffProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staffProfile, setStaffProfile] = useState<StaffProfile | null>(null);
  const [applications, setApplications] = useState<StaffApplication[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchStaffProfile = async () => {
    console.log("🚀 Initialisation du chargement du profil staff");
    try {
      setLoading(true);
      console.log("⏳ Appel à staffService.getStaffProfile()");
      try {
        const profile = await staffService.getStaffProfile();
        console.log("📋 Résultat du profil:", profile);
        setStaffProfile(profile);
        
        if (profile) {
          console.log("✅ Profil trouvé, chargement des candidatures");
          // Si le profil existe, récupérer les candidatures
          const apps = await staffService.getStaffApplications();
          console.log("📑 Candidatures récupérées:", apps);
          setApplications(apps);
        } else {
          console.log("ℹ️ Aucun profil trouvé, affichage du formulaire de création");
          setShowCreateForm(true);
        }
      } catch (error: any) {
        // Si l'erreur est un 404, considérer que l'utilisateur n'a pas de profil
        if (error.response && error.response.status === 404) {
          console.log("ℹ️ Erreur 404: L'utilisateur n'a pas encore de profil staff");
          setShowCreateForm(true);
        } else {
          // Pour les autres erreurs, on les propage
          throw error;
        }
      }
    } catch (err) {
      console.error("❌ Erreur détaillée:", err);
      if (err instanceof Error) {
        setError(err.message);
        console.error("❌ Message d'erreur:", err.message);
      } else {
        setError("Une erreur est survenue lors de la récupération des données");
        console.error("❌ Erreur non standard:", err);
      }
    } finally {
      setLoading(false);
      console.log("🏁 Chargement terminé");
    }
  };

  useEffect(() => {
    fetchStaffProfile();
  }, []);

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    fetchStaffProfile();
  };

  const handleUpdateSuccess = () => {
    setEditMode(false);
    fetchStaffProfile();
  };

  const renderProfileCard = () => {
    if (!staffProfile) return null;

    // Convertir les genres en tableau s'ils sont au format chaîne
    let genresArray: string[] = [];
    if (staffProfile && staffProfile.genres) {
      if (typeof staffProfile.genres === 'string') {
        genresArray = (staffProfile.genres as string).split(',').filter((g: string) => g.trim() !== '');
      } else if (Array.isArray(staffProfile.genres)) {
        genresArray = staffProfile.genres as string[];
      }
    }

    // Convertir staffType en majuscules pour l'affichage si nécessaire
    const staffTypeFormatted = staffProfile.staffType.toUpperCase() as keyof typeof STAFF_TYPE_LABELS;
    const isMusicRelatedStaff = ['DJ', 'MUSICIAN', 'SOUND_ENGINEER'].includes(staffTypeFormatted);

    // Sélectionner l'icône appropriée selon le type de staff
    const getStaffIcon = () => {
      switch (staffTypeFormatted) {
        case 'DJ':
          return <IconMusic size={24} />;
        case 'MUSICIAN':
          return <IconMusic size={24} />;
        case 'SOUND_ENGINEER':
          return <IconMusic size={24} />;
        case 'PHOTOGRAPHER':
          return <IconCamera size={24} />;
        case 'SECURITY':
          return <IconShieldCheck size={24} />;
        case 'BARTENDER':
          return <IconGlass size={24} />;
        default:
          return <IconBriefcase size={24} />;
      }
    };

    return (
      <div className={styles.profileCard}>
        <div className={styles.profileHeader}>
          <h2 className={styles.profileTitle}>
            {getStaffIcon()}
            {STAFF_TYPE_LABELS[staffTypeFormatted] || staffProfile.staffType}
          </h2>
          <div className={styles.statusBadge} data-active={staffProfile.isActive}>
            {staffProfile.isActive ? 'Disponible' : 'Non disponible'}
          </div>
        </div>

        {/* N'afficher les genres que pour les types de staff liés à la musique */}
        {isMusicRelatedStaff && genresArray.length > 0 && (
          <div className={styles.profileSection}>
            <h3 className={styles.sectionTitle}>
              <IconVinyl size={20} />
              Genres musicaux
            </h3>
            <div className={styles.genresContainer}>
              {genresArray.map(genre => {
                const genreKey = genre.toUpperCase() as keyof typeof MUSIC_GENRE_LABELS;
                return (
                  <span key={genre} className={styles.genreBadge}>
                    {MUSIC_GENRE_LABELS[genreKey] || genre}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.profileSection}>
          <h3 className={styles.sectionTitle}>
            <IconStar size={20} />
            Expérience
          </h3>
          <p className={styles.textContent}>{staffProfile.experience}</p>
        </div>

        {staffProfile.equipmentDescription && (
          <div className={styles.profileSection}>
            <h3 className={styles.sectionTitle}>
              <IconDevices size={20} />
              Équipement
            </h3>
            <p className={styles.textContent}>{staffProfile.equipmentDescription}</p>
          </div>
        )}

        <div className={styles.profileSection}>
          <h3 className={styles.sectionTitle}>
            <IconClockHour4 size={20} />
            Disponibilité
          </h3>
          <p className={styles.textContent}>{staffProfile.availability}</p>
        </div>

        {staffProfile.hourlyRate && (
          <div className={styles.profileSection}>
            <h3 className={styles.sectionTitle}>
              <IconCurrencyEuro size={20} />
              Tarif horaire
            </h3>
            <p className={styles.priceTag}>{staffProfile.hourlyRate}</p>
          </div>
        )}

        {(staffProfile.soundcloudUrl || 
          staffProfile.spotifyUrl || 
          staffProfile.bandcampUrl || 
          staffProfile.mixcloudUrl || 
          staffProfile.instagramUrl || 
          staffProfile.portfolio) && (
          <div className={styles.profileSection}>
            <h3 className={styles.sectionTitle}>
              <IconLink size={20} />
              Liens
            </h3>
            <div className={styles.socialLinks}>
              {staffProfile.portfolio && (
                <a href={staffProfile.portfolio} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconWorld size={16} />
                  Portfolio
                </a>
              )}
              {staffProfile.soundcloudUrl && (
                <a href={staffProfile.soundcloudUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconBrandSoundcloud size={16} />
                  SoundCloud
                </a>
              )}
              {staffProfile.spotifyUrl && (
                <a href={staffProfile.spotifyUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconBrandSpotify size={16} />
                  Spotify
                </a>
              )}
              {staffProfile.bandcampUrl && (
                <a href={staffProfile.bandcampUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconBrandBandcamp size={16} />
                  Bandcamp
                </a>
              )}
              {staffProfile.mixcloudUrl && (
                <a href={staffProfile.mixcloudUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconCloud size={16} />
                  Mixcloud
                </a>
              )}
              {staffProfile.instagramUrl && (
                <a href={staffProfile.instagramUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                  <IconBrandInstagram size={16} />
                  Instagram
                </a>
              )}
            </div>
          </div>
        )}

        <div className={styles.profileActions}>
          <button 
            className={styles.editButton}
            onClick={() => setEditMode(true)}
          >
            <IconEdit size={18} />
            Modifier mon profil
          </button>
        </div>
      </div>
    );
  };

  const renderApplications = () => {
    if (!applications.length) {
      return (
        <div className={styles.emptyState}>
          <IconBriefcase size={48} />
          <p>Vous n'avez pas encore postulé pour des événements</p>
          <button className={styles.viewDetailsButton} onClick={() => navigate('/events')}>
            Découvrir les événements
          </button>
        </div>
      );
    }

    return (
      <div className={styles.applicationsContainer}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionHeading}>
            <IconBriefcase size={24} />
            Mes candidatures 
          </h2>
        </div>
        <div className={styles.applicationsList}>
          {applications.map((application) => (
            <div key={application.id} className={styles.applicationCard}>
              <div className={styles.applicationStatus} data-status={application.status}>
                {application.status === 'PENDING' && 'En attente'}
                {application.status === 'ACCEPTED' && 'Acceptée'}
                {application.status === 'REJECTED' && 'Refusée'}
              </div>
              <h3 className={styles.applicationTitle}>
                {application.staffRequest?.event?.title || 'Événement sans titre'}
              </h3>
              <div className={styles.applicationMeta}>
                <span>
                  <IconCalendarEvent size={16} style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'text-bottom' }} />
                  {new Date(application.staffRequest?.event?.dateTime || '').toLocaleDateString()}
                </span>
              </div>
              <div className={styles.applicationMessage}>
                {application.message}
              </div>
              <div className={styles.applicationActions}>
                <button 
                  className={styles.viewDetailsButton}
                  onClick={() => application.staffRequest?.eventId && navigate(`/events/${application.staffRequest.eventId}`)}
                >
                  Voir l'événement
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <IconMusic size={24} />
            Mon profil de staff
          </h1>
          <p className={styles.subtitle}>
            Gérez votre profil de staff et vos candidatures pour des événements
          </p>
        </div>

        <ProfileNavigation />

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Chargement des données...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <IconAlertCircle size={24} />
            <p>{error}</p>
          </div>
        ) : (
          <>
            {showCreateForm ? (
              <StaffProfileForm onSubmitSuccess={handleCreateSuccess} />
            ) : editMode && staffProfile ? (
              <StaffProfileForm existingProfile={staffProfile} onSubmitSuccess={handleUpdateSuccess} />
            ) : staffProfile ? (
              <>
                {renderProfileCard()}
                {renderApplications()}
              </>
            ) : (
              <div className={styles.createProfileContainer}>
                <div className={styles.createProfileCard}>
                  <IconMusic size={48} className={styles.createIcon} />
                  <h2 className={styles.createTitle}>Devenez staff pour des événements</h2>
                  <p className={styles.createDescription}>
                    En tant que DJ, musicien ou autre staff, créez votre profil pour être découvert par des organisateurs d'événements et postuler pour des opportunités.
                  </p>
                  <button 
                    className={styles.createButton}
                    onClick={() => setShowCreateForm(true)}
                  >
                    <IconPlus size={20} />
                    Créer mon profil de staff
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default StaffProfilePage;
